<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import type { TodoCalendar } from '@/api'
import { useTodosStore } from '@/stores/todos'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

type Grouping = 'account' | 'none'

const GROUPING_KEY = 'todos.grouping'
const COLLAPSED_KEY = 'todos.collapsedAccounts'

// Per-device view preferences only -- losing them (private mode, cleared storage) is harmless.
function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

function writeStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage unavailable -- the preference just won't stick
  }
}

const { t } = useI18n()
const store = useTodosStore()
const error = ref<string | null>(null)
const loading = ref(false)
const grouping = ref<Grouping>(readStorage<Grouping>(GROUPING_KEY, 'account'))
const collapsed = ref(new Set<number>(readStorage<number[]>(COLLAPSED_KEY, [])))

watch(grouping, (value) => writeStorage(GROUPING_KEY, value))

const accounts = computed(() => {
  const groups = new Map<
    number,
    { id: number; name: string; readonly: boolean; calendars: TodoCalendar[] }
  >()
  for (const calendar of store.calendars) {
    const group = groups.get(calendar.account_id) ?? {
      id: calendar.account_id,
      name: calendar.account_name,
      readonly: calendar.readonly,
      calendars: [],
    }
    group.calendars.push(calendar)
    groups.set(calendar.account_id, group)
  }
  return [...groups.values()]
})
const showGroupingSwitch = computed(() => accounts.value.length > 1)
const effectiveGrouping = computed<Grouping>(() =>
  showGroupingSwitch.value ? grouping.value : 'none',
)
const flatCalendars = computed(() =>
  [...store.calendars].sort(
    (a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name),
  ),
)

function toggleAccount(id: number): void {
  const next = new Set(collapsed.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  collapsed.value = next
  writeStorage(COLLAPSED_KEY, [...next])
}

async function load(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    await store.loadCalendars()
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <section class="calendars-page">
    <header class="page-header">
      <h1>{{ t('calendars.title') }}</h1>
      <div
        v-if="showGroupingSwitch"
        class="ui-segmented"
        role="group"
        :aria-label="t('calendars.groupBy')"
      >
        <button type="button" :aria-pressed="grouping === 'account'" @click="grouping = 'account'">
          {{ t('calendars.byAccount') }}
        </button>
        <button type="button" :aria-pressed="grouping === 'none'" @click="grouping = 'none'">
          {{ t('calendars.all') }}
        </button>
      </div>
    </header>

    <p v-if="error" class="ui-error">
      {{ error }}
      <button type="button" class="ui-btn" @click="load">{{ t('common.retry') }}</button>
    </p>
    <p v-else-if="loading && !store.calendarsLoaded" class="ui-hint">{{ t('common.loading') }}</p>
    <div v-else-if="store.calendarsLoaded && !store.calendars.length" class="empty">
      <p>{{ t('calendars.none') }}</p>
      <i18n-t keypath="calendars.noneHint" tag="p" class="ui-hint">
        <template #accounts>
          <RouterLink to="/settings/accounts">{{ t('accounts.title') }}</RouterLink>
        </template>
      </i18n-t>
    </div>

    <template v-else-if="effectiveGrouping === 'account'">
      <UiGroup v-for="account in accounts" :key="account.id">
        <button
          type="button"
          class="account-header"
          :aria-expanded="!collapsed.has(account.id)"
          @click="toggleAccount(account.id)"
        >
          <span class="chevron">{{ collapsed.has(account.id) ? '▸' : '▾' }}</span>
          <span class="account-name">{{ account.name }}</span>
          <span v-if="account.readonly" class="ui-chip" :title="t('calendars.readonlyAccount')">{{
            t('calendars.readonlyChip')
          }}</span>
          <span class="ui-hint">{{ account.calendars.length }}</span>
        </button>
        <UiList v-if="!collapsed.has(account.id)">
          <UiRow
            v-for="calendar in account.calendars"
            :key="calendar.id"
            :to="`/todos/${calendar.id}`"
            class="calendar"
          >
            <span class="calendar-name">{{ calendar.name }}</span>
            <span v-if="calendar.is_default" :title="t('calendars.defaultList')">⭐</span>
            <span class="ui-hint">{{ calendar.cached_task_count }}</span>
            <span class="go">›</span>
          </UiRow>
        </UiList>
      </UiGroup>
    </template>

    <UiGroup v-else>
      <UiList>
        <UiRow
          v-for="calendar in flatCalendars"
          :key="calendar.id"
          :to="`/todos/${calendar.id}`"
          class="calendar"
        >
          <span class="calendar-name">
            {{ calendar.name }}
            <span v-if="showGroupingSwitch" class="ui-hint account-hint">{{
              calendar.account_name
            }}</span>
          </span>
          <span v-if="calendar.readonly" :title="t('calendars.readonlyAccount')">🔒</span>
          <span v-if="calendar.is_default" :title="t('calendars.defaultList')">⭐</span>
          <span class="ui-hint">{{ calendar.cached_task_count }}</span>
          <span class="go">›</span>
        </UiRow>
      </UiList>
    </UiGroup>
  </section>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

h1 {
  margin: 0;
  font-size: 1.4rem;
}

.account-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin: 0.75rem 0 0.4rem;
  padding: 0.2rem 0.25rem;
  border: none;
  background: none;
  color: var(--tg-section-header-text-color);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.account-name {
  flex: 1;
  text-align: left;
}

.chevron {
  width: 1rem;
}

.calendar {
  color: var(--tg-text-color);
  text-decoration: none;
}

.calendar-name {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.account-hint {
  font-size: 0.78rem;
}

.go {
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}

.empty p {
  margin: 0.3rem 0;
}
</style>
